import { parseCardExpiry } from '@/lib/billing';
import { useCallback, useEffect, useState } from "react";

declare global {
    interface Window {
        acceptJsCallback?: (response: AcceptJsResponse) => void;
    }
}

export interface AcceptJsCreditCardResult {
    nonce: AcceptJsOpaqueData;
    cardLast4: string;
    cardType: string;
    customerFirstName?: string;
    customerLastName?: string;
    /** From the hosted form's expiry; null if it couldn't be read. */
    expiryMonth: number | null;
    expiryYear: number | null;
};

export interface UseAcceptJsProps {
    environment: 'sandbox' | 'production';
}

export interface UseAcceptJsReturn {
    buttonRef: (node: HTMLButtonElement | null) => void;
    scriptLoaded: boolean;
    scriptError: string | null;
    paymentError: string | null;
    creditCardResult: AcceptJsCreditCardResult | null;
    reset: () => void;
    retry: () => void;
}
export interface AcceptJsOpaqueData {
    dataDescriptor: string;
    dataValue: string;
}
export interface AcceptJsResponse {
    messages: {
        resultCode: 'Ok' | 'Error';
        message: AcceptUiMessage[];
    };
    opaqueData: AcceptJsOpaqueData;
    encryptedCardData?: {
        cardNumber: string;
        expDate: string;
        bin?: string;
    };
    customerInformation?: {
        firstName?: string;
        lastName?: string;
    };
}

export interface AcceptUiMessage {
    code: string;
    text: string;
}

const ACCEPT_JS_SCRIPT_ID = 'accept-js-script';

export const useAcceptJs = ({ environment }: UseAcceptJsProps): UseAcceptJsReturn => {
    const [buttonNode, setButtonNode] = useState<HTMLButtonElement | null>(null);
    const [scriptLoaded, setScriptLoaded] = useState(false);
    const [scriptError, setScriptError] = useState<string | null>(null);
    const [paymentError, setPaymentError] = useState<string | null>(null);
    const [attempt, setAttempt] = useState(0);
    const [paymentResult, setPaymentResult] = useState<AcceptJsCreditCardResult | null>(null);

    useEffect(() => {

        if (!buttonNode) {
            return;
        }
        window.acceptJsCallback = (response: AcceptJsResponse) => {
            if (response.messages.resultCode !== 'Ok') {
                if (response.messages.message && response.messages.message.length > 0) {
                    setPaymentError(response.messages.message.map(msg => msg?.text).join(', '));
                } else {
                    setPaymentError('Unable to validate the card. Please check the details and try again.');
                }
                setPaymentResult(null);
                return;
            }
            const result = transformAcceptJsResponse(response);
            setPaymentError(result ? null : 'Unable to validate the card. Please try again.');
            setPaymentResult(result);
        }

        const script = document.createElement('script');
        script.id = ACCEPT_JS_SCRIPT_ID;
        script.src = environment === 'sandbox' ? 'https://jstest.authorize.net/v3/AcceptUI.js' : 'https://js.authorize.net/v3/AcceptUI.js';
        script.onload = () => setScriptLoaded(true);
        script.onerror = () => setScriptError('Failed to load payment service. Please try again later.');
        document.body.appendChild(script);

        return () => {
            delete window.acceptJsCallback;
            script.remove();
            document.getElementById('AcceptUIContainer')?.remove();
            setScriptLoaded(false);
        }

    }, [buttonNode, environment, attempt]);

    const reset = useCallback(() => setPaymentResult(null), []);
    const retry = useCallback(() => {
        setScriptLoaded(false);
        setScriptError(null);
        setPaymentError(null);
        setAttempt(current => current + 1);
    }, []);

    return {
        buttonRef: setButtonNode,
        scriptLoaded,
        scriptError,
        paymentError,
        creditCardResult: paymentResult,
        reset,
        retry,
    };
}

const transformAcceptJsResponse = (response: AcceptJsResponse): AcceptJsCreditCardResult | null => {
    if (response.messages.resultCode === 'Ok' && response.opaqueData) {
        const maskedCardNumber = response.encryptedCardData?.cardNumber || '';
        const cardLast4 = maskedCardNumber.slice(-4);
        const cardType = getCardTypeFromBin(response.encryptedCardData?.bin || '');
        const expiry = parseCardExpiry(response.encryptedCardData?.expDate);
        return {
            nonce: response.opaqueData,
            cardLast4,
            cardType,
            customerFirstName: response.customerInformation?.firstName,
            customerLastName: response.customerInformation?.lastName,
            expiryMonth: expiry.month,
            expiryYear: expiry.year,
        };
    }
    return null;
}
const getCardTypeFromBin = (bin: string): string => {
    const cardTypes: { [key: string]: string } = {
        '4': 'Visa',
        '5': 'MasterCard',
        '3': 'American Express',
        '6': 'Discover',
    };
    return cardTypes[bin.charAt(0)] || 'Unknown';
};
